// ── Экран входа и регистрации ────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { BrandMark, ThemeButtons } from "../components/Bits";
import { t, uiLocale } from "../i18n";
import { hasAppBridge, openExternal } from "../nativeBridge";
import { requestPasswordReset, supabase } from "../supabase";
import { SANS } from "../themes";
import { usePalette } from "../ui/theme";
import { Field, TextButton, Button as UiButton } from "../ui";
import { isElectron } from "../utils";
import { panelSurface } from '../ui/surface';

// ── AuthScreen — экран входа / регистрации ────────────────────────────────────

// Понятные сообщения вместо сырых ошибок Supabase. Раньше при входе любая ошибка
// показывалась как «Неверный email или пароль» — даже неподтверждённая почта.
export function authErrorText(e){
  const m=String(e?.message||"").toLowerCase();const code=e?.code||"";
  if(e?.status===0||m.includes("fetch"))return t("Нет соединения. Проверьте интернет.");
  if(code==="over_email_send_rate_limit"||m.includes("rate limit"))return t("Сейчас нельзя отправить письмо: почтовый сервис отправляет всего несколько писем в час. Подождите час — или войдите через Google.");
  if(code==="email_not_confirmed"||m.includes("not confirmed"))return t("Почта ещё не подтверждена. Откройте письмо и перейдите по ссылке — или отправьте письмо ещё раз.");
  if(code==="user_already_exists"||m.includes("already registered"))return t("Такой аккаунт уже есть. Войдите или восстановите пароль.");
  if(code==="invalid_credentials"||m.includes("invalid login"))return t("Неверный email или пароль.");
  if(code==="same_password"||m.includes("different from the old"))return t("Новый пароль совпадает с прежним — придумайте другой.");
  if(code==="weak_password"&&/pwned|leak|known|guess/.test(m))return t("Этот пароль слишком простой или уже встречался в утечках — придумайте другой.");
  if(code==="weak_password"||m.includes("password should"))return t("Пароль слишком простой — минимум 6 символов.");
  if(code==="reauthentication_needed"||m.includes("reauthenticat"))return t("Для смены пароля войдите заново и повторите.");
  if(code==="email_address_invalid"||(m.includes("invalid")&&m.includes("email")))return t("В адресе почты ошибка.");
  if(code==="signup_disabled")return t("Регистрация по почте выключена.");
  if(m.includes("provider is not enabled")||m.includes("unsupported provider"))return t("Вход через Google ещё не включён в настройках Supabase.");
  return e?.message||t("Ошибка");
}

export function AuthScreen({theme,onTheme}){
  const C=usePalette();
  const [mode,setMode]=useState("login"); // "login" | "register" | "forgot"
  const [email,setEmail]=useState("");
  const [pw,setPw]=useState("");
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState("");
  const [ok,setOk]=useState("");
  const [needConfirm,setNeedConfirm]=useState(""); // почта, на которую можно отправить письмо ещё раз
  const isNativeApp=typeof window!=="undefined"&&window.Capacitor?.isNativePlatform?.();
  // Почему человек снова на экране входа: админ заблокировал или «выкинул».
  const [notice]=useState(()=>{try{const n=JSON.parse(localStorage.getItem("ac_auth_notice")||"null");localStorage.removeItem("ac_auth_notice");return n;}catch{return null;}});
  // Ссылка из письма устарела или уже использована
  const [linkErr]=useState(()=>{try{const e=sessionStorage.getItem("ac_auth_link_error");sessionStorage.removeItem("ac_auth_link_error");return e;}catch{return null;}});
  // Вход после этой минуты — новый сеанс: старое «выкинуть» его уже не касается.
  useEffect(()=>{try{localStorage.setItem("ac_login_at",new Date().toISOString());}catch{}},[]);
  const googleLogin=async()=>{
    setErr("");setOk("");setLoading(true);
    // Телефон: Google не пускает вход внутри приложения — открываем браузер телефона,
    // после входа он вернёт в приложение по ссылке com.audiocards.app://auth-callback.
    if(isNativeApp){
      const{data,error:e}=await supabase.auth.signInWithOAuth({provider:"google",options:{redirectTo:"com.audiocards.app://auth-callback",skipBrowserRedirect:true}});
      if(e||!data?.url){setErr(authErrorText(e||new Error("no url")));setLoading(false);return;}
      try{await openExternal(data.url);setOk(t("Завершите вход в открывшемся браузере — потом приложение откроется само."));}
      catch(er){setErr(t("Не удалось завершить вход: {0}",[er?.message||String(er)]));}
      setLoading(false);return;
    }
    const{error:e}=await supabase.auth.signInWithOAuth({provider:"google",options:{redirectTo:window.location.origin+"/"}});
    if(e){setErr(authErrorText(e));setLoading(false);}
    // Компьютер: вход идёт в отдельном окне, его можно закрыть — кнопка снова доступна.
    else if(isElectron())setTimeout(()=>setLoading(false),1500);
  };
  const resend=async()=>{
    setErr("");setOk("");setLoading(true);
    const{error:e}=await supabase.auth.resend({type:"signup",email:needConfirm});
    if(e)setErr(authErrorText(e));else setOk(t("Письмо отправлено ещё раз на {0}.",[needConfirm]));
    setLoading(false);
  };
  const submit=async()=>{
    setErr("");setOk("");setLoading(true);
    if(mode==="forgot"){
      if(!email){setErr(t("Введите email"));setLoading(false);return;}
      try{
        const{error:e}=await requestPasswordReset(email);
        if(e)setErr(authErrorText(e));
        else setOk(t("Письмо отправлено на {0}. Откройте его и перейдите по ссылке.",[email]));
      }catch{setErr(t("Нет соединения."));}
      setLoading(false);return;
    }
    if(!email||!pw){setErr(t("Заполни email и пароль"));setLoading(false);return;}
    try{
      if(mode==="register"){
        const{data,error:e}=await supabase.auth.signUp({email:email.trim(),password:pw});
        if(e)setErr(authErrorText(e));
        else if(data?.session)setOk(t("Аккаунт создан!"));
        else if(data?.user&&!(data.user.identities||[]).length)setErr(t("Такой аккаунт уже есть. Войдите или восстановите пароль."));
        else{setOk(t("Письмо для подтверждения отправлено на {0}. Откройте его и перейдите по ссылке. Нет письма — проверьте «Спам».",[email.trim()]));setNeedConfirm(email.trim());}
      }else{
        const{error:e}=await supabase.auth.signInWithPassword({email:email.trim(),password:pw});
        if(e){setErr(authErrorText(e));if(e.code==="email_not_confirmed"||/not confirmed/i.test(e.message||""))setNeedConfirm(email.trim());}
      }
    }catch{setErr(t("Нет соединения. Проверьте интернет."));}
    setLoading(false);
  };
  const title = mode==="login"?t("Войдите, чтобы открыть проекты")
    : mode==="register"?t("Создайте аккаунт — это бесплатно")
    : t("Сбросить пароль по email");
  const btnLabel = mode==="login"?t("Войти") : mode==="register"?t("Создать аккаунт") : t("Отправить письмо");
  const go=(m)=>{setMode(m);setErr("");setOk("");};
  const note=(color,children)=>(
    <div role="alert" style={{background:C.surface,border:`1px solid ${color}`,borderRadius:10,padding:"10px 12px",fontSize:13.5,lineHeight:1.5,color:C.text,marginBottom:14}}>{children}</div>
  );
  const onEnter=(e)=>{if(e.key==="Enter")submit();};
  return(
    <div style={{minHeight:"100vh",background:C.backdrop,fontFamily:SANS,display:"flex",alignItems:"center",justifyContent:"center",padding:20,boxSizing:"border-box",position:"relative"}}>
      <div style={{position:"absolute",top:16,right:16,zIndex:1}}><ThemeButtons theme={theme} onTheme={onTheme}/></div>
      <div data-panel="" style={{...panelSurface(C),borderRadius:12,padding:"36px 28px",width:"100%",maxWidth:360,boxSizing:"border-box",boxShadow:`0 8px 40px ${C.shadowMd}`,position:"relative",zIndex:1}}>
        <div style={{display:"flex",alignItems:"center",gap:11,fontSize:24,fontWeight:600,color:C.text,marginBottom:6,letterSpacing:"-0.01em"}}><BrandMark size={36}/>AntonCards</div>
        <div style={{fontSize:13,color:C.mid,marginBottom:22}}>{title}</div>
        {notice&&note(notice.kind==="ban"?C.danger:C.border,notice.kind==="ban"
          ?<>{t("Аккаунт заблокирован")} {new Date(notice.until).getFullYear()>=2999?t("навсегда"):t("до {0}",[new Date(notice.until).toLocaleString(uiLocale(),{day:"numeric",month:"long",hour:"2-digit",minute:"2-digit"})])}.{notice.reason?<> {t("Причина:")} {notice.reason}.</>:null}</>
          :t("Администратор завершил ваш сеанс. Войдите снова."))}
        {linkErr&&note(C.warm,t("Ссылка из письма устарела или уже использована. Нажмите «Забыли пароль?» — придёт новая."))}
        {mode==="login"&&isNativeApp&&!hasAppBridge()&&(
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 12px",fontSize:13,lineHeight:1.5,color:C.mid,marginBottom:14}}>
            {t("Вход через Google есть в новой версии приложения. Если вы входили через Google, нажмите «Забыли пароль?»: придёт письмо, задайте пароль — и входите по почте.")}
          </div>
        )}
        {mode!=="forgot"&&(!isNativeApp||hasAppBridge())&&(<>
          <UiButton full size="lg" onClick={googleLogin} disabled={loading} style={{marginBottom:14,fontWeight:600}}>
            <span style={{display:"inline-flex",alignItems:"center",gap:10}}>
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              {t("Продолжить с Google")}
            </span>
          </UiButton>
          <div style={{display:"flex",alignItems:"center",gap:10,color:C.faint,fontSize:12,marginBottom:14}}><span style={{flex:1,height:1,background:C.borderSoft}}/>{t("или по почте")}<span style={{flex:1,height:1,background:C.borderSoft}}/></div>
        </>)}
        <Field label="Email" type="email" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={onEnter} placeholder={t("name@example.com")} autoComplete="email"/>
        {mode!=="forgot"&&(
          <Field label={t("Пароль")} type="password" value={pw} onChange={e=>setPw(e.target.value)} onKeyDown={onEnter} placeholder={t("минимум 6 символов")}
            autoComplete={mode==="register"?"new-password":"current-password"}/>
        )}
        {mode==="login"&&(
          <div style={{textAlign:"right",marginTop:-6,marginBottom:12}}>
            <TextButton size={12.5} onClick={()=>go("forgot")}>{t("Забыли пароль?")}</TextButton>
          </div>
        )}
        {err&&<div role="alert" style={{background:C.surface,border:`1px solid ${C.danger}`,borderRadius:9,padding:"9px 13px",fontSize:13,color:C.danger,marginBottom:14,overflowWrap:"break-word"}}>{err}</div>}
        {ok&&<div role="status" style={{background:C.surface,border:`1px solid ${C.green}`,borderRadius:9,padding:"9px 13px",fontSize:13,color:C.green,marginBottom:14,overflowWrap:"break-word"}}>{ok}</div>}
        {needConfirm&&mode!=="forgot"&&(
          <div style={{textAlign:"center",marginTop:-6,marginBottom:12}}>
            <TextButton size={12.5} onClick={resend}>{t("Отправить письмо ещё раз")}</TextButton>
          </div>
        )}
        <UiButton full size="lg" variant="primary" onClick={submit} busy={loading} style={{marginBottom:16,fontWeight:500}}>
          {loading?t("Загрузка..."):btnLabel}
        </UiButton>
        <div style={{textAlign:"center",fontSize:13,color:C.mid,lineHeight:1.6}}>
          {mode==="forgot"?(
            <TextButton bold onClick={()=>go("login")}>{t("← Назад к входу")}</TextButton>
          ):(
            <>
              {mode==="login"?t("Нет аккаунта? "):t("Уже есть аккаунт? ")}
              <TextButton bold onClick={()=>go(mode==="login"?"register":"login")}>{mode==="login"?t("Зарегистрироваться"):t("Войти")}</TextButton>
            </>
          )}
        </div>
        {mode==="register" && (
          <div style={{textAlign:"center",fontSize:12,color:C.faint,marginTop:14,lineHeight:2}}>
            {t("Регистрируясь, вы соглашаетесь с")}{' '}
            <a href="/legal/terms.html"   target="_blank" rel="noreferrer" style={{color:C.mid}}>Terms of Service</a>{' · '}
            <a href="/legal/privacy.html" target="_blank" rel="noreferrer" style={{color:C.mid}}>Privacy Policy</a>{' · '}
            <a href="/legal/dmca.html"    target="_blank" rel="noreferrer" style={{color:C.mid}}>DMCA</a>
          </div>
        )}
      </div>
    </div>
  );
}
